import { useChangeFilterTrack } from "@/_old/features/analytics";
import { Button } from "@/core/components/ui/button";
import { Checkbox } from "@/core/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/core/components/ui/command";
import { Label } from "@/core/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/core/components/ui/popover";
import { BOOKING_STATUS_LABELS } from "@/core/utils";
import { useDashboardFilters } from "@/dashboard/store/useDashboardFilters";
import { ChevronDown } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface StatusFilterProps extends React.ComponentPropsWithoutRef<
  typeof Popover
> {
  location: string;
  onSubmit?: boolean;
  onChange?: (value: number[] | null) => void;
}

export function StatusFilter({
  location,
  onSubmit,
  onChange,
  ...props
}: StatusFilterProps) {
  const { t } = useTranslation(["dashboard", "core"]);
  const { onChangeFilter } = useChangeFilterTrack();
  const { filters, setFilters } = useDashboardFilters();
  const [selectedItem, setSelectedItem] = useState<
    | {
        id: number;
        name: string;
      }[]
    | null
  >(
    filters.status?.map((status: number) => ({
      id: status,
      name: t(
        BOOKING_STATUS_LABELS[status as keyof typeof BOOKING_STATUS_LABELS]
          .label as string,
      ),
    })) || null,
  );

  const handleChange = useCallback(
    (value: { id: number; name: string }[] | null) => {
      //   onChangeFilter("FILTER_MEMBERS", eventCategory);

      const statusIds = value
        ? value.map((status: { id: number }) => status.id).filter(Boolean)
        : [];

      if (onSubmit) {
        onChange?.(statusIds as number[]);
      } else {
        setFilters({ status: statusIds });
      }
    },
    [onChange, onSubmit, setFilters],
  );

  const handleSelect = useCallback(
    (status: { id: number; name: string } | null) => {
      if (status === null) {
        setSelectedItem(null);
        handleChange(null);
      } else {
        setSelectedItem((prev) => {
          if (!prev) {
            const newSelection = [status];
            handleChange(newSelection);
            return newSelection;
          }

          const isAlreadySelected = prev.some(
            (selectedStatus: { id: number; name: string }) =>
              selectedStatus.id === status.id,
          );

          if (isAlreadySelected) {
            const newSelection = prev.filter(
              (selectedStatus: { id: number; name: string }) =>
                selectedStatus.id !== status.id,
            );
            handleChange(newSelection.length > 0 ? newSelection : null);
            return newSelection.length > 0 ? newSelection : null;
          } else {
            const newSelection = [...prev, status];
            handleChange(newSelection);
            return newSelection;
          }
        });
      }
    },
    [handleChange],
  );

  const data: { id: number; name: string }[] = Object.entries(
    BOOKING_STATUS_LABELS,
  ).map(([key, type]) => ({
    id: type.value as number,
    name: t(type.label) as string,
  }));

  useEffect(() => {
    setSelectedItem(
      filters.status?.map((status: number) => ({
        id: status,
        name: t(
          BOOKING_STATUS_LABELS[status as keyof typeof BOOKING_STATUS_LABELS]
            .label as string,
        ) as string,
      })) || null,
    );
  }, [filters.status]);

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={`${location}-status-filter`}>
        {t("status-filter-label") as string}
      </Label>

      <Popover {...props}>
        <PopoverTrigger asChild>
          <Button
            id={`${location}-status-filter`}
            variant="outline"
            className="justify-between w-full bg-white"
          >
            <span className="overflow-hidden font-normal text-ellipsis">
              {selectedItem && selectedItem.length > 0
                ? selectedItem.length === 1
                  ? `${selectedItem[0].name}`
                  : (t("status-filter-selected-multiple", {
                      count: selectedItem.length,
                    }) as string)
                : (t("status-filter-placeholder") as string)}
            </span>
            <ChevronDown className="opacity-50 size-4" />
          </Button>
        </PopoverTrigger>

        <PopoverContent className="p-0 w-[500px] max-w-[500px]">
          <Command>
            <CommandInput
              placeholder={t("status-filter-search-placeholder") as string}
              className="h-9"
            />
            <CommandList>
              <CommandEmpty>{t("status-filter-empty-state")}</CommandEmpty>
              <CommandGroup>
                <CommandItem
                  value=""
                  onSelect={() => {
                    handleSelect(null);
                  }}
                >
                  <Checkbox
                    className="mr-2"
                    checked={!selectedItem || selectedItem.length === 0}
                  />

                  {t("status-filter-placeholder")}
                </CommandItem>
                {data.map((status, index) => (
                  <CommandItem
                    key={`${status.id}-${index}`}
                    value={status.name}
                    onSelect={() => {
                      handleSelect(status);
                    }}
                  >
                    <Checkbox
                      className="mr-2"
                      checked={
                        selectedItem?.some(
                          (selectedMember) => selectedMember.id === status.id,
                        ) ||
                        selectedItem?.length === 0 ||
                        !selectedItem
                      }
                    />

                    {status.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
